import React, { useEffect, useState, useRef, Children, memo, Component } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { Check, X, ChevronDown, ArrowRight, Globe, Palette, PenTool, Layout, Target, Search, User, Clock, Ear, Map, Paintbrush, Rocket, Instagram, MessageCircle } from 'lucide-react';
import { translations } from './translations';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/qdsbbqZJ3r1i8ghs5agscn/Foto_de_Perfil_de_Facebook_Dia_de_la_Mujer_Vibrante_Rosado_(1).png';
// --- Animation Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = '',
  duration = 0.8
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  duration?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration,
  delay,
  ease: [0.22, 1, 0.36, 1]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const StaggerContainer = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={{
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  }} className={className} data-id="element-1">
      {children}
    </motion.div>;
};
const StaggerItem = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
};
const WordReveal = ({
  text
}: {
  text: string;
}) => {
  const words = text.split(' ');
  return <span className="inline-block" data-id="element-3">
      {words.map((word, i) => <span key={i} className="inline-block overflow-hidden mr-[0.25em]" data-id="element-4">
          <motion.span className="inline-block" initial={{
        y: '100%'
      }} whileInView={{
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: i * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-5">
            {word}
          </motion.span>
        </span>)}
    </span>;
};
const FloatingParticles = () => {
  return <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-6">
      {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-cyan/30 rounded-full" initial={{
      x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
      y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
      scale: Math.random() * 2 + 0.5
    }} animate={{
      y: [null, Math.random() * -500],
      x: [null, (Math.random() - 0.5) * 200],
      opacity: [0, 1, 0]
    }} transition={{
      duration: Math.random() * 10 + 10,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-7" />)}
    </div>;
};
const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState(5);
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (inView && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [inView, timeLeft]);
  return <div ref={ref} className="text-8xl md:text-9xl font-serif font-bold text-negative/20 relative" data-id="element-8">
      00:0{timeLeft}
      <motion.div className="absolute inset-0 text-negative" initial={{
      clipPath: 'inset(0 100% 0 0)'
    }} animate={{
      clipPath: `inset(0 ${(5 - timeLeft) / 5 * 100}% 0 0)`
    }} transition={{
      duration: 1,
      ease: 'linear'
    }} data-id="element-9">
        00:0{timeLeft}
      </motion.div>
    </div>;
};
const AnimatedCounter = ({
  value,
  label,
  prefix = '',
  suffix = ''
}: {
  value: number;
  label: string;
  prefix?: string;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (inView) {
      let start = 0;
      const duration = 2000;
      const increment = value / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [inView, value]);
  return <div ref={ref} className="text-center" data-id="element-10">
      <div className="font-serif text-5xl md:text-6xl font-bold text-cyan mb-2" data-id="element-11">
        {prefix}
        {count}
        {suffix}
      </div>
      <div className="text-muted text-sm uppercase tracking-widest" data-id="element-12">
        {label}
      </div>
    </div>;
};
const ProgressBar = ({
  label,
  percentage,
  isNegative = false
}: {
  label: string;
  percentage: number;
  isNegative?: boolean;
}) => {
  return <div className="mb-6" data-id="element-13">
      <div className="flex justify-between text-sm mb-2" data-id="element-14">
        <span className="text-white/80" data-id="element-15">{label}</span>
        <span className={isNegative ? 'text-negative' : 'text-cyan'} data-id="element-16">
          {percentage}%
        </span>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden" data-id="element-17">
        <motion.div className={`h-full ${isNegative ? 'bg-negative' : 'bg-cyan'}`} initial={{
        width: 0
      }} whileInView={{
        width: `${percentage}%`
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} data-id="element-18" />
      </div>
    </div>;
};
// --- Main App Component ---
export function App() {
  const [lang, setLang] = useState<'es' | 'en'>('es');
  const t = translations[lang];
  const scrollToCTA = () => {
    document.getElementById('cta-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="film-grain min-h-screen bg-background text-white font-sans overflow-x-hidden selection:bg-cyan/40" data-id="element-19">
      {/* 1. STICKY NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-id="element-20">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-21">
          <div className="flex items-center gap-3" data-id="element-22">
            <img src={LOGO_URL} alt="Profumind Logo" className="w-10 h-10 rounded-full border border-cyan/20" data-id="element-23" />
            <span className="font-serif font-semibold text-xl tracking-wide text-cyan" data-id="element-24">
              PROFUMIND
            </span>
          </div>
          <div className="flex items-center gap-4" data-id="element-25">
            <div className="flex items-center gap-2 bg-surface border border-white/10 rounded-full p-1" data-id="element-26">
              <button onClick={() => setLang('es')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${lang === 'es' ? 'bg-cyan text-background' : 'text-muted hover:text-white'}`} data-id="element-27">
                ES
              </button>
              <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${lang === 'en' ? 'bg-cyan text-background' : 'text-muted hover:text-white'}`} data-id="element-28">
                EN
              </button>
            </div>
            <button onClick={scrollToCTA} className="glow-border hidden sm:flex items-center gap-2 bg-surface text-white px-6 py-2.5 rounded-full font-medium text-sm transition-transform hover:scale-105" data-id="element-29">
              {t.nav.hablemos} <ArrowRight className="w-4 h-4 text-cyan" data-id="element-30" />
            </button>
          </div>
        </div>
      </nav>

      <main data-id="element-31">
        {/* 2. HERO */}
        <section className="relative h-screen min-h-[800px] flex items-center justify-center pt-20 overflow-hidden" data-id="element-32">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80')] bg-cover bg-center z-0" data-id="element-33" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40 z-0" data-id="element-34" />
          <FloatingParticles data-id="element-35" />

          {/* Subtle Lens Flare */}
          <motion.div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-cyan/10 blur-[120px] rounded-full pointer-events-none z-0" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 2
        }} data-id="element-36" />

          <div className="max-w-5xl mx-auto px-6 relative z-10 text-center flex flex-col items-center" data-id="element-37">
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-8 flex flex-col items-center gap-2" data-id="element-38">
              <FadeIn delay={0.2} data-id="element-39">
                <span className="block text-white" data-id="element-40">{t.hero.title1}</span>
              </FadeIn>
              <FadeIn delay={0.4} data-id="element-41">
                <span className="block text-white/80 text-4xl md:text-6xl" data-id="element-42">
                  {t.hero.title2}
                </span>
              </FadeIn>
              <FadeIn delay={0.6} data-id="element-43">
                <span className="block text-cyan mt-4 text-6xl md:text-8xl" data-id="element-44">
                  {t.hero.title3}
                </span>
              </FadeIn>
            </h1>

            <FadeIn delay={1} className="mb-12" data-id="element-45">
              <p className="text-xl md:text-2xl text-muted font-light max-w-2xl mx-auto" data-id="element-46">
                {t.hero.subtitle}
              </p>
            </FadeIn>

            <FadeIn delay={1.2} className="flex flex-col items-center gap-4" data-id="element-47">
              <button onClick={scrollToCTA} className="glow-border group relative inline-flex items-center justify-center gap-3 bg-surface text-white px-10 py-4 rounded-full text-lg font-semibold transition-transform hover:scale-105" data-id="element-48">
                {t.hero.cta}{' '}
                <ArrowRight className="w-5 h-5 text-cyan group-hover:translate-x-1 transition-transform" data-id="element-49" />
              </button>
              <span className="text-sm text-muted italic font-light" data-id="element-50">
                {t.hero.ctaSub}
              </span>
            </FadeIn>
          </div>

          <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-cyan/50 z-10" style={{
          animation: 'scrollPulse 2s infinite'
        }} data-id="element-51">
            <div className="w-px h-16 bg-gradient-to-b from-transparent via-cyan to-transparent" data-id="element-52" />
            <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 mt-4" data-id="element-53">
              {t.hero.scroll}
            </span>
          </motion.div>
        </section>

        {/* 3. THE PROBLEM - Split Screen */}
        <section className="bg-background py-32 relative border-t border-white/5" data-id="element-54">
          <div className="max-w-6xl mx-auto px-6" data-id="element-55">
            <FadeIn data-id="element-56">
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-20 text-center max-w-4xl mx-auto leading-tight" data-id="element-57">
                {t.problem.title}
              </h2>
            </FadeIn>

            {/* Split Screen Cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-24 overflow-hidden" data-id="element-58">
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8
            }} className="bg-surface p-10 rounded-3xl border border-negative/30 relative grayscale-[50%]" data-id="element-59">
                <div className="absolute top-0 right-0 w-32 h-32 bg-negative/5 blur-[40px] rounded-full" data-id="element-60" />
                <h3 className="text-2xl font-serif text-white mb-8 flex items-center gap-3" data-id="element-61">
                  <X className="text-negative w-8 h-8" data-id="element-62" /> {t.problem.badTitle}
                </h3>
                <ul className="space-y-6 text-muted text-lg" data-id="element-63">
                  {t.problem.badPoints.map((point, i) => <li key={i} className="flex items-center gap-3" data-id="element-64">
                      <div className="w-2 h-2 rounded-full bg-negative/50" data-id="element-65" />{' '}
                      {point}
                    </li>)}
                </ul>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="bg-surface p-10 rounded-3xl border border-cyan/50 relative shadow-[0_0_30px_rgba(0,229,255,0.1)]" data-id="element-66">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan/10 blur-[40px] rounded-full" data-id="element-67" />
                <h3 className="text-2xl font-serif text-white mb-8 flex items-center gap-3" data-id="element-68">
                  <Check className="text-cyan w-8 h-8" data-id="element-69" /> {t.problem.goodTitle}
                </h3>
                <ul className="space-y-6 text-white/90 text-lg" data-id="element-70">
                  {t.problem.goodPoints.map((point, i) => <li key={i} className="flex items-center gap-3" data-id="element-71">
                      <div className="w-2 h-2 rounded-full bg-cyan" data-id="element-72" /> {point}
                    </li>)}
                </ul>
              </motion.div>
            </div>

            {/* Flowchart */}
            <FadeIn className="max-w-4xl mx-auto bg-surface2/50 rounded-3xl p-8 md:p-16 border border-white/5 mb-20" data-id="element-73">
              <div className="flex flex-col items-center mb-12" data-id="element-74">
                <p className="text-xl text-muted mb-6" data-id="element-75">
                  {t.problem.flowchart.start}
                </p>
                <div className="flex items-center gap-6 text-cyan" data-id="element-76">
                  <User className="w-8 h-8" data-id="element-77" />
                  <motion.div className="w-16 h-px bg-cyan" initial={{
                  scaleX: 0
                }} whileInView={{
                  scaleX: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1
                }} style={{
                  originX: 0
                }} data-id="element-78" />
                  <Search className="w-8 h-8" data-id="element-79" />
                </div>
                <div className="w-px h-12 bg-white/20 mt-6" data-id="element-80" />
              </div>

              <div className="grid md:grid-cols-2 gap-16 relative max-w-4xl mx-auto" data-id="element-81">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-white/20 hidden md:block" data-id="element-82" />

                {/* Left Path */}
                <div className="text-center relative pt-8 md:pt-0" data-id="element-83">
                  <motion.div className="absolute top-0 left-1/2 w-px h-10 bg-negative -translate-x-1/2 hidden md:block" initial={{
                  scaleY: 0
                }} whileInView={{
                  scaleY: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 1
                }} style={{
                  originY: 0
                }} data-id="element-84" />
                  <div className="bg-surface border border-negative/30 p-10 rounded-2xl mb-10 inline-block w-full" data-id="element-85">
                    <h4 className="text-white font-semibold text-xl mb-6" data-id="element-86">
                      {t.problem.flowchart.badPath.title}
                    </h4>
                    <ul className="text-base text-muted space-y-3 text-left w-fit mx-auto" data-id="element-87">
                      {t.problem.flowchart.badPath.points.map((point, i) => <li key={i} data-id="element-88">{point}</li>)}
                    </ul>
                  </div>
                  <motion.div className="w-px h-10 bg-negative mx-auto mb-6" initial={{
                  scaleY: 0
                }} whileInView={{
                  scaleY: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 1.5
                }} style={{
                  originY: 0
                }} data-id="element-89" />
                  <div className="text-negative font-bold text-2xl" data-id="element-90">
                    {t.problem.flowchart.badPath.result}
                  </div>
                </div>

                {/* Right Path */}
                <div className="text-center relative pt-8 md:pt-0" data-id="element-91">
                  <motion.div className="absolute top-0 left-1/2 w-px h-10 bg-cyan -translate-x-1/2 hidden md:block" initial={{
                  scaleY: 0
                }} whileInView={{
                  scaleY: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 1
                }} style={{
                  originY: 0
                }} data-id="element-92" />
                  <div className="bg-surface border border-cyan/50 p-10 rounded-2xl mb-10 inline-block w-full shadow-[0_0_20px_rgba(0,229,255,0.1)]" data-id="element-93">
                    <h4 className="text-white font-semibold text-xl mb-6" data-id="element-94">
                      {t.problem.flowchart.goodPath.title}
                    </h4>
                    <ul className="text-base text-white/80 space-y-3 text-left w-fit mx-auto" data-id="element-95">
                      {t.problem.flowchart.goodPath.points.map((point, i) => <li key={i} data-id="element-96">{point}</li>)}
                    </ul>
                  </div>
                  <motion.div className="w-px h-10 bg-cyan mx-auto mb-6" initial={{
                  scaleY: 0
                }} whileInView={{
                  scaleY: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 1.5
                }} style={{
                  originY: 0
                }} data-id="element-97" />
                  <div className="text-cyan font-bold text-2xl" data-id="element-98">
                    {t.problem.flowchart.goodPath.result}
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn className="max-w-3xl mx-auto text-center" data-id="element-99">
              <p className="text-xl md:text-2xl text-muted leading-relaxed mb-6" data-id="element-100">
                {t.problem.conclusion1}
              </p>
              <p className="text-2xl text-white font-medium" data-id="element-101">
                {t.problem.conclusion2}
              </p>
              <div className="flex justify-center" data-id="element-102">
                <Countdown data-id="element-103" />
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 4. THE MECHANISM - Layer Reveal */}
        <section className="bg-surface py-32 border-y border-white/5 overflow-hidden" data-id="element-104">
          <div className="max-w-6xl mx-auto px-6" data-id="element-105">
            <div className="text-center mb-24" data-id="element-106">
              <FadeIn data-id="element-107">
                <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-6 flex items-center justify-center gap-2" data-id="element-108">
                  {t.mechanism.title}{' '}
                  <span className="text-cyan text-2xl relative -top-4" data-id="element-109">
                    ™
                    <motion.span className="absolute inset-0 bg-cyan blur-sm" animate={{
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} data-id="element-110" />
                  </span>
                </h2>
                <p className="text-xl text-muted max-w-2xl mx-auto mb-12" data-id="element-111">
                  {t.mechanism.subtitle}
                </p>
              </FadeIn>

              <div className="font-serif text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-cyan tracking-[0.1em] sm:tracking-[0.2em] md:tracking-[0.3em] mb-8" data-id="element-112">
                <WordReveal text={t.mechanism.word} data-id="element-113" />
              </div>

              <FadeIn delay={0.5} data-id="element-114">
                <p className="text-lg text-white/80 italic" data-id="element-115">
                  {t.mechanism.quote}
                </p>
              </FadeIn>
            </div>

            {/* Comparison Cards */}
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-20" data-id="element-116">
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="bg-background p-10 rounded-3xl border border-white/5" data-id="element-117">
                <h3 className="text-xl font-serif text-muted mb-8 text-center tracking-widest uppercase" data-id="element-118">
                  {t.mechanism.badCard.title}
                </h3>
                <ul className="space-y-5 text-muted text-lg" data-id="element-119">
                  {t.mechanism.badCard.points.map((point, i) => <li key={i} className="flex items-center gap-4" data-id="element-120">
                      <span className="text-white/20 text-2xl" data-id="element-121">○</span> {point}
                    </li>)}
                </ul>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="bg-surface2 p-10 rounded-3xl border border-cyan shadow-[0_0_40px_rgba(0,229,255,0.15)] relative overflow-hidden" data-id="element-122">
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan/10 blur-[60px] rounded-full pointer-events-none" data-id="element-123" />
                <h3 className="text-xl font-serif text-cyan mb-8 text-center tracking-widest uppercase relative z-10" data-id="element-124">
                  {t.mechanism.goodCard.title}
                </h3>
                <ul className="space-y-5 text-white text-lg relative z-10" data-id="element-125">
                  {t.mechanism.goodCard.points.map((point, i) => <li key={i} className="flex items-center gap-4" data-id="element-126">
                      <span className="text-cyan text-2xl" data-id="element-127">●</span> {point}
                    </li>)}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. SERVICES - 3D Hover Cards */}
        <section className="bg-background py-32 border-y border-white/5" data-id="element-128">
          <div className="max-w-6xl mx-auto px-6" data-id="element-129">
            <FadeIn data-id="element-130">
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-20 text-center" data-id="element-131">
                {t.services.title}
              </h2>
            </FadeIn>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto" data-id="element-132">
              {t.services.items.map((service, i) => {
              const icons = [Globe, Palette, PenTool, Layout, Target];
              const Icon = icons[i];
              return <StaggerItem key={i} className={i === 4 ? 'md:col-span-2 flex justify-center' : ''} data-id="element-133">
                    <motion.div whileHover={{
                  scale: 1.02,
                  rotateX: 2,
                  rotateY: -2
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20
                }} className={`bg-surface p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/5 hover:border-cyan/50 relative overflow-hidden group shadow-lg hover:shadow-[0_20px_40px_rgba(0,229,255,0.1)] ${i === 4 ? 'w-full md:max-w-[calc(50%-0.75rem)]' : 'w-full'}`} data-id="element-134">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan/0 to-cyan/0 group-hover:from-cyan/5 group-hover:to-transparent transition-colors duration-500" data-id="element-135" />

                      <div className="flex items-start gap-5 relative z-10" data-id="element-136">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan/20 transition-colors duration-500" data-id="element-137">
                          <Icon className="w-6 h-6 md:w-7 md:h-7 text-muted group-hover:text-cyan transition-colors duration-500" data-id="element-138" />
                        </div>

                        <div data-id="element-139">
                          <h3 className="text-lg md:text-xl font-serif font-semibold text-white mb-1 tracking-wide" data-id="element-140">
                            {service.title}
                          </h3>
                          <p className="text-cyan text-sm font-medium mb-2" data-id="element-141">
                            {service.sub}
                          </p>
                          <div className="h-px w-12 bg-cyan/50 mb-3 group-hover:w-full transition-all duration-700 ease-out" data-id="element-142" />
                          <p className="text-muted font-light leading-relaxed text-sm md:text-base" data-id="element-143">
                            {service.desc}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </StaggerItem>;
            })}
            </StaggerContainer>
          </div>
        </section>

        {/* 6. PROCESS - Horizontal Timeline */}
        <section className="bg-surface py-32 border-y border-white/5 overflow-hidden" data-id="element-144">
          <div className="max-w-6xl mx-auto px-6" data-id="element-145">
            <FadeIn data-id="element-146">
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-24 text-center" data-id="element-147">
                {t.process.title}
              </h2>
            </FadeIn>

            <div className="relative" data-id="element-148">
              {/* Desktop Line */}
              <div className="hidden md:block absolute top-[45px] left-[10%] right-[10%] h-px bg-white/10" data-id="element-149">
                <motion.div className="h-full bg-gradient-to-r from-cyan via-cyan to-cyan" initial={{
                width: 0
              }} whileInView={{
                width: '100%'
              }} viewport={{
                once: true,
                margin: '-100px'
              }} transition={{
                duration: 1.5,
                ease: 'easeInOut'
              }} data-id="element-150" />
              </div>

              {/* Mobile Line */}
              <div className="md:hidden absolute top-[10%] bottom-[10%] left-[45px] w-px bg-white/10" data-id="element-151">
                <motion.div className="w-full bg-gradient-to-b from-cyan via-cyan to-cyan" initial={{
                height: 0
              }} whileInView={{
                height: '100%'
              }} viewport={{
                once: true,
                margin: '-100px'
              }} transition={{
                duration: 1.5,
                ease: 'easeInOut'
              }} data-id="element-152" />
              </div>

              <div className="flex flex-col md:flex-row justify-between gap-12 md:gap-4 relative z-10" data-id="element-153">
                {t.process.items.map((step, i) => {
                const icons = [Ear, Map, Paintbrush, Rocket];
                const Icon = icons[i];
                return <motion.div key={i} className="flex md:flex-col items-center md:text-center gap-6 md:gap-6 flex-1" initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  duration: 0.6,
                  delay: i * 0.3
                }} data-id="element-154">
                      <div className="w-24 h-24 rounded-full bg-background border-2 border-surface2 flex items-center justify-center shrink-0 relative group" data-id="element-155">
                        <div className="absolute inset-0 rounded-full border-2 border-cyan scale-110 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500" data-id="element-156" />
                        <Icon className="w-8 h-8 text-cyan" data-id="element-157" />
                      </div>
                      <div data-id="element-158">
                        <div className="text-cyan font-serif text-xl mb-2" data-id="element-159">
                          {step.num}
                        </div>
                        <h3 className="text-white font-semibold tracking-widest uppercase mb-3" data-id="element-160">
                          {step.title}
                        </h3>
                        <p className="text-muted text-sm font-light leading-relaxed max-w-[200px] mx-auto" data-id="element-161">
                          {step.desc}
                        </p>
                      </div>
                    </motion.div>;
              })}
              </div>
            </div>
          </div>
        </section>

        {/* 7. SOCIAL PROOF - Counters & Progress Bars */}
        <section className="bg-background py-32 relative" data-id="element-162">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-163" />

          <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-164">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24" data-id="element-165">
              <AnimatedCounter prefix="+" value={40} label={t.socialProof.counters[0]} data-id="element-166" />
              <AnimatedCounter value={100} suffix="%" label={t.socialProof.counters[1]} data-id="element-167" />
              <AnimatedCounter value={5} suffix="seg" label={t.socialProof.counters[2]} data-id="element-168" />
            </div>

            <div className="flex justify-center mb-24" data-id="element-169">
              <div className="w-full max-w-md h-px bg-gradient-to-r from-transparent via-cyan to-transparent relative" data-id="element-170">
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-cyan rounded-full blur-[2px]" animate={{
                opacity: [0.5, 1, 0.5],
                scale: [1, 1.5, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-171" />
              </div>
            </div>

            <FadeIn data-id="element-172">
              <h3 className="font-serif text-3xl text-center text-white mb-16" data-id="element-173">
                {t.socialProof.title}
              </h3>

              <div className="grid md:grid-cols-2 gap-16 max-w-4xl mx-auto" data-id="element-174">
                <div className="bg-surface/50 p-8 rounded-3xl border border-negative/20" data-id="element-175">
                  <h4 className="text-negative font-serif text-xl mb-8 uppercase tracking-widest text-center" data-id="element-176">
                    {t.socialProof.before}
                  </h4>
                  <ProgressBar label={t.socialProof.bars[0]} percentage={20} isNegative data-id="element-177" />
                  <ProgressBar label={t.socialProof.bars[1]} percentage={10} isNegative data-id="element-178" />
                  <ProgressBar label={t.socialProof.bars[2]} percentage={15} isNegative data-id="element-179" />
                </div>

                <div className="bg-surface2 p-8 rounded-3xl border border-cyan/30 shadow-[0_0_30px_rgba(0,229,255,0.08)]" data-id="element-180">
                  <h4 className="text-cyan font-serif text-xl mb-8 uppercase tracking-widest text-center" data-id="element-181">
                    {t.socialProof.after}
                  </h4>
                  <ProgressBar label={t.socialProof.bars[0]} percentage={80} data-id="element-182" />
                  <ProgressBar label={t.socialProof.bars[1]} percentage={90} data-id="element-183" />
                  <ProgressBar label={t.socialProof.bars[2]} percentage={85} data-id="element-184" />
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* 8. PARA QUIÉN ES - Identity Cards */}
        <section className="bg-surface py-32 border-y border-white/5" data-id="element-185">
          <div className="max-w-5xl mx-auto px-6" data-id="element-186">
            <div className="text-center mb-20" data-id="element-187">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-white" data-id="element-188">
                <WordReveal text={t.identity.title} data-id="element-189" />
              </h2>
            </div>

            <StaggerContainer className="grid md:grid-cols-2 gap-8" data-id="element-190">
              {t.identity.items.map((item, i) => <StaggerItem key={i} data-id="element-191">
                  <div className="bg-background p-10 rounded-3xl border border-white/5 hover:border-cyan/30 transition-all duration-300 hover:-translate-y-2 group" data-id="element-192">
                    <div className="flex items-start gap-6" data-id="element-193">
                      <div className="w-10 h-10 rounded-full bg-cyan/10 flex items-center justify-center shrink-0 group-hover:bg-cyan/20 transition-colors" data-id="element-194">
                        <Check className="w-5 h-5 text-cyan" data-id="element-195" />
                      </div>
                      <div data-id="element-196">
                        <h3 className="text-white font-semibold tracking-widest uppercase mb-3" data-id="element-197">
                          {item.title}
                        </h3>
                        <p className="text-muted font-light leading-relaxed" data-id="element-198">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </StaggerItem>)}
            </StaggerContainer>
          </div>
        </section>

        {/* 9. CTA FINAL - Full Screen Cinematic */}
        <section id="cta-section" className="relative h-screen min-h-[800px] flex items-center justify-center overflow-hidden" data-id="element-199">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80')] bg-cover bg-center z-0" data-id="element-200" />
          <div className="absolute inset-0 bg-background/90 z-0" data-id="element-201" />
          <FloatingParticles data-id="element-202" />

          <motion.div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rounded-full z-0" initial={{
          opacity: 0,
          scale: 0,
          boxShadow: '0 0 0px rgba(0,229,255,0)'
        }} whileInView={{
          opacity: 1,
          scale: 1,
          boxShadow: '0 0 100px 20px rgba(0,229,255,0.5)'
        }} viewport={{
          once: true
        }} transition={{
          duration: 2
        }} data-id="element-203" />

          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col items-center" data-id="element-204">
            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-16" data-id="element-205">
              <WordReveal text={t.cta.title} data-id="element-206" />
            </h2>

            <FadeIn delay={1} data-id="element-207">
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6" data-id="element-208">
                <a href="https://wa.me/message/QBLVAHVTJJWDF1" target="_blank" rel="noopener noreferrer" className="glow-border group relative inline-flex items-center justify-center gap-4 bg-surface text-white px-10 py-5 rounded-full text-xl font-semibold transition-transform hover:scale-105" data-id="element-209">
                  <MessageCircle className="w-6 h-6 text-cyan" data-id="element-210" />
                  {t.cta.whatsapp}
                  <ArrowRight className="w-6 h-6 text-cyan group-hover:translate-x-2 transition-transform" data-id="element-211" />
                </a>
                <a href="https://www.instagram.com/profumindarg/?utm_source=ig_web_button_share_sheet" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-3 bg-surface border border-cyan/30 text-white px-8 py-5 rounded-full text-lg font-semibold transition-all hover:scale-105 hover:border-cyan/60 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]" data-id="element-212">
                  <Instagram className="w-6 h-6 text-cyan" data-id="element-213" />
                  {t.cta.instagram}
                </a>
              </div>
              <p className="text-muted italic font-light mb-16" data-id="element-214">{t.cta.sub}</p>
            </FadeIn>

            <FadeIn delay={1.5} className="w-full max-w-2xl" data-id="element-215">
              <div className="h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent mb-12" data-id="element-216" />
              <p className="text-2xl md:text-3xl font-serif italic text-cyan mb-16 leading-relaxed" data-id="element-217">
                {t.cta.quote}
              </p>

              <motion.div className="inline-block relative" animate={{
              filter: ['drop-shadow(0 0 10px rgba(0,229,255,0.2))', 'drop-shadow(0 0 30px rgba(0,229,255,0.6))', 'drop-shadow(0 0 10px rgba(0,229,255,0.2))']
            }} transition={{
              duration: 4,
              repeat: Infinity
            }} data-id="element-218">
                <img src={LOGO_URL} alt="Profumind Logo" className="w-20 h-20 rounded-full border border-cyan/30" data-id="element-219" />
              </motion.div>
            </FadeIn>
          </div>
        </section>

        {/* 10. FAQ */}
        <section className="bg-surface py-32 border-y border-white/5" data-id="element-220">
          <div className="max-w-3xl mx-auto px-6" data-id="element-221">
            <FadeIn data-id="element-222">
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-16 text-center" data-id="element-223">
                {t.faq.title}
              </h2>
              <div className="space-y-4" data-id="element-224">
                {t.faq.items.map((item, i) => <FaqItem key={i} question={item.q} answer={item.a} data-id="element-225" />)}
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* 11. FOOTER */}
      <footer className="bg-background py-16 text-center relative overflow-hidden" data-id="element-226">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-32 bg-cyan/5 blur-[50px] pointer-events-none" data-id="element-227" />
        <div className="max-w-3xl mx-auto px-6 relative z-10" data-id="element-228">
          <img src={LOGO_URL} alt="Profumind Logo" className="w-12 h-12 rounded-full mx-auto mb-8 opacity-40 hover:opacity-100 transition-all duration-500 cursor-pointer" data-id="element-229" />
          <p className="font-serif italic text-xl text-cyan mb-8" data-id="element-230">
            {t.footer.quote}
          </p>
          <div className="flex items-center justify-center gap-6 mb-8" data-id="element-231">
            <a href="https://wa.me/message/QBLVAHVTJJWDF1" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-cyan/50 hover:bg-cyan/10 transition-all duration-300 group" aria-label="WhatsApp" data-id="element-232">
              <MessageCircle className="w-5 h-5 text-muted group-hover:text-cyan transition-colors" data-id="element-233" />
            </a>
            <a href="https://www.instagram.com/profumindarg/?utm_source=ig_web_button_share_sheet" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-cyan/50 hover:bg-cyan/10 transition-all duration-300 group" aria-label="Instagram" data-id="element-234">
              <Instagram className="w-5 h-5 text-muted group-hover:text-cyan transition-colors" data-id="element-235" />
            </a>
          </div>
          <p className="text-xs text-muted/50 tracking-widest uppercase" data-id="element-236">
            {t.footer.copyright}
          </p>
        </div>
      </footer>
    </div>;
}
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-white/5 rounded-2xl bg-background overflow-hidden hover:border-cyan/30 transition-colors" data-id="element-237">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-238">
        <span className="font-medium text-lg text-white" data-id="element-239">{question}</span>
        <ChevronDown className={`w-5 h-5 text-cyan transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} data-id="element-240" />
      </button>
      <AnimatePresence data-id="element-241">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-242">
            <div className="px-8 pb-8 text-muted font-light leading-relaxed pt-2" data-id="element-243">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}